import { useMemo, useState } from 'react'

type RecordRow = Record<string, unknown>

type Irregularidade = {
  id: number | string
  codigo: number
  descricao: string
  valor: number
}

type Props = {
  records: RecordRow[]
  source: 'online' | 'cache'
  savedAt: string | null
  loading: boolean
  error: string
}

function text(value: unknown) {
  return value == null ? '' : String(value).trim()
}

function normalize(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function toNumber(value: unknown) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0
  const normalized = text(value).replace(/\./g, '').replace(',', '.')
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : 0
}

function formatBRL(value: number) {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function toItem(row: RecordRow, index: number): Irregularidade {
  return {
    id: (row.id as number | string | undefined) ?? index,
    codigo: Number(row.codigo) || 0,
    descricao: text(row.descricao) || 'Descrição não informada',
    valor: toNumber(row.valor),
  }
}

export function IrregularidadesOperational({ records, source, savedAt, loading, error }: Props) {
  const [query, setQuery] = useState('')

  const items = useMemo(
    () => records.map(toItem).sort((a, b) => a.codigo - b.codigo),
    [records],
  )

  const filtered = useMemo(() => {
    const needle = normalize(query)
    if (!needle) return items

    return items.filter((item) => {
      const valor = formatBRL(item.valor)
      const variants = [
        item.codigo,
        item.descricao,
        valor,
        item.valor.toFixed(2),
        item.valor.toFixed(2).replace('.', ','),
      ].join(' ')
      return normalize(variants).includes(needle)
    })
  }, [items, query])

  return (
    <section className="irregularidades-tool">
      <div className="tool-heading">
        <div>
          <p className="eyebrow">CONSULTA OPERACIONAL</p>
          <h2>Irregularidades</h2>
          <p className="muted">Consulte códigos, descrições e valores de ocorrências de irregularidade publicados no Suite.</p>
        </div>
        <span className={`status-chip ${source === 'cache' ? 'warning' : ''}`}>
          {source === 'cache' ? 'Contingência' : 'Base publicada'}
        </span>
      </div>

      <section className="irregularidades-card">
        <div className="irregularidades-toolbar">
          <label>
            Filtrar irregularidades
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Ex.: 407, clandestina, lacre, hidrômetro..."
              autoFocus
            />
          </label>
          <div className="irregularidades-toolbar-actions">
            <span><strong>{filtered.length}</strong> de {items.length} item(ns)</span>
            <button className="secondary-button" type="button" onClick={() => setQuery('')}>Limpar filtro</button>
          </div>
        </div>

        {source === 'cache' && savedAt && (
          <p className="irregularidades-cache">Última sincronização local: {new Date(savedAt).toLocaleString('pt-BR')}</p>
        )}

        {loading && <div className="empty-state">Carregando irregularidades...</div>}
        {error && <div className="notice error">{error}</div>}

        {!loading && !error && filtered.length === 0 && (
          <div className="empty-state">Nenhuma irregularidade encontrada com esse filtro.</div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div className="irregularidades-table-wrap">
            <table className="irregularidades-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Descrição</th>
                  <th>Valor</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => (
                  <tr key={item.id}>
                    <td><strong>{item.codigo}</strong></td>
                    <td>{item.descricao}</td>
                    <td><strong>{formatBRL(item.valor)}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </section>
  )
}
